import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InsightsPanel } from '@/components/dashboard/InsightsPanel';
import { TeamActivityFeed } from '@/components/dashboard/TeamActivityFeed';
import { AuditTrailPanel } from '@/components/dashboard/AuditTrailPanel';
import {
  InsightsPanelSkeleton,
} from '@/components/dashboard/skeletons/DashboardSkeletons';
import { ActiveSession, TeamActivity } from '@/hooks/useDashboardData';
import { BarChart3, Users, ShieldCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface DashboardSidebarProps {
  sessions: ActiveSession[];
  teamActivities: TeamActivity[];
  workspaceId: string | null;
  isLoading: boolean;
  activeInsightsTab: string;
  onInsightsTabChange: (tab: string) => void;
}

export function DashboardSidebar({
  sessions,
  teamActivities,
  workspaceId,
  isLoading,
  activeInsightsTab,
  onInsightsTabChange,
}: DashboardSidebarProps) {
  const [activeTab, setActiveTab] = useState('insights');

  if (isLoading) {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-card">
        <div className="p-4 border-b border-border/50">
          <div className="flex gap-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton-shimmer h-9 flex-1 rounded-lg" />
            ))}
          </div>
        </div>
        <div className="p-6">
          <InsightsPanelSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-xl sm:rounded-2xl border border-border/50 bg-card transition-all duration-500 hover:border-primary/20">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-32 sm:w-40 h-32 sm:h-40 bg-primary/5 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="relative z-10">
        <div className="p-3 sm:p-4 border-b border-border/50">
          <TabsList className="w-full grid grid-cols-3 bg-muted/30 h-9 sm:h-10">
            <TabsTrigger 
              value="insights" 
              className="gap-1 sm:gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm text-xs sm:text-sm px-1 sm:px-3"
            >
              <BarChart3 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">Insights</span>
            </TabsTrigger>
            <TabsTrigger 
              value="team" 
              className="gap-1 sm:gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm text-xs sm:text-sm px-1 sm:px-3"
            >
              <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">Team</span>
              {teamActivities.length > 0 && (
                <Badge variant="secondary" className="ml-0.5 sm:ml-1 h-4 w-4 sm:h-5 sm:w-5 p-0 flex items-center justify-center text-[9px] sm:text-[10px] bg-primary/10 text-primary">
                  {teamActivities.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="audit" 
              className="gap-1 sm:gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm text-xs sm:text-sm px-1 sm:px-3"
            >
              <ShieldCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">Audit</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="insights" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
          <InsightsPanel 
            sessions={sessions}
            activeTab={activeInsightsTab}
            onTabChange={onInsightsTabChange}
            embedded
          />
        </TabsContent>

        <TabsContent value="team" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
          <TeamActivityFeed activities={teamActivities} embedded />
        </TabsContent>

        <TabsContent value="audit" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
          {workspaceId && <AuditTrailPanel workspaceId={workspaceId} embedded />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
